import React, { useState, useEffect } from 'react';
import { Bell, BellOff, StickyNote, Plus, Pencil, Trash2, Check, X, Loader, User } from 'lucide-react';
import toast from 'react-hot-toast';
import AccountAccordionItem from './AccountAccordionItem';
import { accounts } from '../../services/apiService';
import { TextField } from '@anyemp/ui-kit';

/**
 * Компонент для отображения социального аккаунта с секциями Notifications и Notes.
 * Оборачивает AccountAccordionItem и добавляет контент через слот extraContent.
 *
 * Props:
 *   account                 - объект соцаккаунта (с is_notifications)
 *   accountKey              - уникальный ключ
 *   isExpanded              - открыт ли аккордеон
 *   onToggle                - fn(accountKey)
 *   index                   - порядковый индекс (для animation-delay)
 *   badges                  - ReactNode — дополнительные бейджи
 *   canViewPassword         - bool
 *   selfMode                - bool — использовать self-эндпоинты (MyProfile) или admin-эндпоинты
 *   canToggleNotifications  - bool
 *   canViewNotes            - bool
 *   canAddNotes             - bool
 *   canEditNotes            - bool
 *   canDeleteNotes          - bool
 *   currentUserId           - string — ID текущего пользователя для проверки принадлежности заметок
 *   onNotificationsChange   - fn(socialAccountId, newValue) — оптимистичное обновление в родителе
 */
const SocialAccountAccordionItem = ({
  account,
  accountKey,
  isExpanded,
  onToggle,
  index = 0,
  badges,
  canViewPassword = true,
  selfMode = false,
  canToggleNotifications = false,
  canViewNotes = false,
  canAddNotes = false,
  canEditNotes = false,
  canDeleteNotes = false,
  currentUserId,
  onNotificationsChange,
}) => {
  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [notesError, setNotesError] = useState(null);
  const [notesFetched, setNotesFetched] = useState(false);

  const [newNoteText, setNewNoteText] = useState('');
  const [addingNote, setAddingNote] = useState(false);

  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editNoteText, setEditNoteText] = useState('');
  const [savingNoteId, setSavingNoteId] = useState(null);

  const [deletingNoteId, setDeletingNoteId] = useState(null);
  const [togglingNotifications, setTogglingNotifications] = useState(false);

  // Выбор API-методов в зависимости от режима
  const api = selfMode ? {
    getNotes: accounts.getSocialAccountNotesSelf,
    createNote: accounts.createSocialAccountNoteSelf,
    updateNote: accounts.updateSocialAccountNoteSelf,
    deleteNote: accounts.deleteSocialAccountNoteSelf,
    updateNotifications: accounts.updateSocialAccountNotificationsSelf,
  } : {
    getNotes: accounts.getSocialAccountNotes,
    createNote: accounts.createSocialAccountNote,
    updateNote: accounts.updateSocialAccountNote,
    deleteNote: accounts.deleteSocialAccountNote,
    updateNotifications: accounts.updateSocialAccountNotifications,
  };

  // Загрузка заметок при первом раскрытии аккордеона
  useEffect(() => {
    if (isExpanded && canViewNotes && !notesFetched && !notesLoading) {
      fetchNotes();
    }
  }, [isExpanded, canViewNotes]);

  const fetchNotes = async () => {
    setNotesLoading(true);
    setNotesError(null);
    try {
      const result = await api.getNotes(account.id);
      if (result.success) {
        setNotes(result.data || []);
        setNotesFetched(true);
      } else {
        setNotesError(result.error || 'Failed to load notes');
      }
    } catch {
      setNotesError('Failed to load notes');
    } finally {
      setNotesLoading(false);
    }
  };

  const handleToggleNotifications = async () => {
    const newValue = !account.is_notifications;
    setTogglingNotifications(true);
    try {
      const result = await api.updateNotifications(account.id, newValue);
      if (result.success) {
        onNotificationsChange?.(account.id, newValue);
        toast.success(newValue ? 'Notifications enabled' : 'Notifications disabled');
      } else {
        toast.error(result.error || 'Failed to update notifications');
      }
    } catch {
      toast.error('Failed to update notifications');
    } finally {
      setTogglingNotifications(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNoteText.trim()) return;
    setAddingNote(true);
    try {
      const result = await api.createNote(account.id, newNoteText.trim());
      if (result.success && result.data) {
        setNotes(prev => [result.data, ...prev]);
        setNewNoteText('');
        toast.success('Note added');
      } else {
        toast.error(result.error || 'Failed to add note');
      }
    } catch {
      toast.error('Failed to add note');
    } finally {
      setAddingNote(false);
    }
  };

  const handleEditNote = (note) => {
    setEditingNoteId(note.id);
    setEditNoteText(note.text);
  };

  const handleSaveNote = async (noteId) => {
    if (!editNoteText.trim()) return;
    setSavingNoteId(noteId);
    try {
      const result = await api.updateNote(account.id, noteId, editNoteText.trim());
      if (result.success && result.data) {
        setNotes(prev => prev.map(n => n.id === noteId ? result.data : n));
        setEditingNoteId(null);
        setEditNoteText('');
        toast.success('Note updated');
      } else {
        toast.error(result.error || 'Failed to update note');
      }
    } catch {
      toast.error('Failed to update note');
    } finally {
      setSavingNoteId(null);
    }
  };

  const handleCancelEdit = () => {
    setEditingNoteId(null);
    setEditNoteText('');
  };

  const handleDeleteNote = async (noteId) => {
    setDeletingNoteId(noteId);
    try {
      const result = await api.deleteNote(account.id, noteId);
      if (result.success) {
        setNotes(prev => prev.filter(n => n.id !== noteId));
        toast.success('Note deleted');
      } else {
        toast.error(result.error || 'Failed to delete note');
      }
    } catch {
      toast.error('Failed to delete note');
    } finally {
      setDeletingNoteId(null);
    }
  };

  const extraContent = isExpanded ? (
    <>
      {/* ─── Notifications ─── */}
      {canToggleNotifications && (
        <div className="pt-4">
          <h5 className="text-sm font-bold text-light-text-primary dark:text-dark-text-primary mb-3 flex items-center">
            <div className="p-1.5 bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg mr-2">
              <Bell className="h-3.5 w-3.5 text-white" />
            </div>
            Notifications
          </h5>
          <div className="bg-light-bg-secondary dark:bg-dark-bg-primary rounded-xl p-4 border border-light-border-primary/50 dark:border-dark-border-primary/50 flex items-center justify-between">
            <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary flex items-center gap-2">
              <Bell className="h-4 w-4" />
              Enable Notifications
            </span>
            <button
              onClick={handleToggleNotifications}
              disabled={togglingNotifications}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 disabled:opacity-50 ${
                account.is_notifications
                  ? 'bg-primary-600 dark:bg-primary-500'
                  : 'bg-light-border-primary dark:bg-dark-border-secondary'
              }`}
              role="switch"
              aria-checked={!!account.is_notifications}
            >
              {togglingNotifications ? (
                <Loader className="h-3.5 w-3.5 text-white animate-spin absolute left-1/2 -translate-x-1/2" />
              ) : (
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform duration-200 ${
                  account.is_notifications ? 'translate-x-6' : 'translate-x-1'
                }`} />
              )}
            </button>
          </div>
        </div>
      )}

      {/* ─── Notes ─── */}
      {canViewNotes && (
        <div className="pt-4">
          <h5 className="text-sm font-bold text-light-text-primary dark:text-dark-text-primary mb-3 flex items-center">
            <div className="p-1.5 bg-gradient-to-br from-teal-500 to-teal-600 rounded-lg mr-2">
              <StickyNote className="h-3.5 w-3.5 text-white" />
            </div>
            Notes
            {notes.length > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 text-xs font-semibold rounded-full">
                {notes.length}
              </span>
            )}
          </h5>

          {notesLoading && (
            <div className="flex items-center gap-2 text-sm text-light-text-tertiary dark:text-dark-text-tertiary py-2">
              <Loader className="h-4 w-4 animate-spin" />
              Loading notes...
            </div>
          )}

          {notesError && (
            <p className="text-xs text-red-500 dark:text-red-400 mb-2">{notesError}</p>
          )}

          {/* Список заметок */}
          {notes.length > 0 && (
            <div className="space-y-2 mb-3">
              {notes.map(note => (
                <div
                  key={note.id}
                  className="bg-light-bg-secondary dark:bg-dark-bg-primary rounded-xl p-3 border border-light-border-primary/50 dark:border-dark-border-primary/50"
                >
                  {editingNoteId === note.id ? (
                    <div className="flex flex-col gap-2">
                      <TextField
                        multiline
                        rows={2}
                        size="small"
                        fullWidth
                        value={editNoteText}
                        onChange={(e) => setEditNoteText(e.target.value)}
                      />
                      <div className="flex gap-2 justify-end">
                        <button
                          onClick={() => handleSaveNote(note.id)}
                          disabled={savingNoteId === note.id || !editNoteText.trim()}
                          className="p-1.5 rounded-lg bg-primary-500 hover:bg-primary-600 text-white disabled:opacity-50 transition-colors"
                          title="Save"
                        >
                          {savingNoteId === note.id ? <Loader className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          disabled={savingNoteId === note.id}
                          className="p-1.5 rounded-lg bg-light-bg-tertiary dark:bg-dark-bg-tertiary hover:bg-light-border-primary dark:hover:bg-dark-border-primary text-light-text-primary dark:text-dark-text-primary transition-colors"
                          title="Cancel"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-light-text-primary dark:text-dark-text-primary whitespace-pre-wrap break-words">
                          {note.text}
                        </p>
                        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-light-text-tertiary dark:text-dark-text-tertiary">
                          {note.created_by_user && (
                            <span className="flex items-center gap-1">
                              <User className="h-3 w-3 flex-shrink-0" />
                              {note.created_by_user.name || note.created_by_user.email}
                            </span>
                          )}
                          {note.updated_by_user && note.updated_by_user.id !== note.created_by_user?.id && (
                            <span className="flex items-center gap-1 italic">
                              edited by {note.updated_by_user.name || note.updated_by_user.email}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        {canEditNotes && note.created_by_user_id === currentUserId && (
                          <button
                            onClick={() => handleEditNote(note)}
                            className="p-1 rounded-lg hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary text-primary-500 dark:text-primary-400 transition-colors"
                            title="Edit note"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                        )}
                        {canDeleteNotes && note.created_by_user_id === currentUserId && (
                          <button
                            onClick={() => handleDeleteNote(note.id)}
                            disabled={deletingNoteId === note.id}
                            className="p-1 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500 dark:text-red-400 transition-colors disabled:opacity-50"
                            title="Delete note"
                          >
                            {deletingNoteId === note.id ? <Loader className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Добавление заметки */}
          {canAddNotes && (
            <div className="flex flex-col gap-2">
              <TextField
                multiline
                rows={3}
                size="small"
                fullWidth
                value={newNoteText}
                onChange={(e) => setNewNoteText(e.target.value)}
                placeholder="Add a note..."
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && e.ctrlKey) {
                    e.preventDefault();
                    handleAddNote();
                  }
                }}
              />
              <div className="flex justify-end">
                <button
                  onClick={handleAddNote}
                  disabled={addingNote || !newNoteText.trim()}
                  className="px-3 py-2 bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 text-white rounded-lg text-sm disabled:opacity-50 flex items-center gap-1 transition-colors"
                >
                  {addingNote ? <Loader className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                  Add
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  ) : null;

  const notificationsBadge = canToggleNotifications ? (
    account.is_notifications ? (
      <span className="hidden sm:inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-lg bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 border border-amber-200 dark:border-amber-700/50">
        <Bell className="h-3 w-3" />
        On
      </span>
    ) : (
      <span className="hidden sm:inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-lg bg-light-bg-tertiary dark:bg-dark-bg-primary text-light-text-tertiary dark:text-dark-text-tertiary border border-light-border-primary/50 dark:border-dark-border-primary/50">
        <BellOff className="h-3 w-3" />
        Off
      </span>
    )
  ) : null;

  return (
    <AccountAccordionItem
      account={account}
      accountKey={accountKey}
      isExpanded={isExpanded}
      onToggle={onToggle}
      index={index}
      badges={<>{notificationsBadge}{badges}</>}
      canViewPassword={canViewPassword}
      extraContent={extraContent}
    />
  );
};

export default SocialAccountAccordionItem;
